import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Mail, Plus, Trash2, Send, ChevronDown, ChevronUp } from 'lucide-react'
import { toast } from 'sonner'
import {
  useReportSchedules,
  useReportHistory,
  useCreateReport,
  useUpdateReport,
  useDeleteReport,
  useSendReport,
} from '../hooks/useReports'

function formatDate(dateStr: string | null | undefined) {
  if (!dateStr) return 'Never'
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatRelative(dateStr: string) {
  const d = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60_000)
  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffH = Math.floor(diffMin / 60)
  if (diffH < 24) return `${diffH}h ago`
  const diffD = Math.floor(diffH / 24)
  return `${diffD}d ago`
}

export function ReportsSection({ siteId }: { siteId: string }) {
  const { data, isLoading } = useReportSchedules(siteId)
  const { data: historyData } = useReportHistory(siteId)
  const createReport = useCreateReport(siteId)
  const updateReport = useUpdateReport(siteId)
  const deleteReport = useDeleteReport(siteId)
  const sendReport = useSendReport(siteId)

  const [showAdd, setShowAdd] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newName, setNewName] = useState('')
  const [newFrequency, setNewFrequency] = useState('weekly')

  if (isLoading) return <Skeleton className="h-32 w-full" />

  const schedules = data?.schedules ?? []
  const history = historyData?.history ?? []

  const handleAdd = () => {
    if (!newEmail.trim()) {
      toast.error('Email address is required')
      return
    }
    createReport.mutate(
      { recipientEmail: newEmail.trim(), recipientName: newName.trim() || undefined, frequency: newFrequency },
      {
        onSuccess: () => {
          toast.success('Report schedule created')
          setShowAdd(false)
          setNewEmail('')
          setNewName('')
          setNewFrequency('weekly')
        },
        onError: () => toast.error('Failed to create report schedule'),
      },
    )
  }

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5" />
              Automated Reports
            </Label>
            <p className="text-xs text-muted-foreground">
              Send periodic SEO summary emails to clients or team members.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setShowAdd(!showAdd)}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add
          </Button>
        </div>

        {/* Add form */}
        {showAdd && (
          <div className="space-y-3 border rounded-md p-3">
            <div className="space-y-1">
              <Label className="text-xs">Recipient Email</Label>
              <Input
                type="email"
                placeholder="client@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Recipient Name (optional)</Label>
              <Input
                placeholder="Jane Smith"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Frequency</Label>
              <Select value={newFrequency} onValueChange={setNewFrequency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} disabled={createReport.isPending}>
                Create Schedule
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Schedule list */}
        {schedules.length === 0 && !showAdd && (
          <p className="text-sm text-muted-foreground py-2">
            No report schedules configured. Add one to start sending automated SEO reports.
          </p>
        )}

        {schedules.map((schedule) => (
          <div key={schedule.id} className="flex items-center justify-between border rounded-md p-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate">{schedule.recipient_email}</span>
                <Badge variant={schedule.enabled ? 'default' : 'secondary'} className="text-[10px] h-4 px-1.5">
                  {schedule.frequency}
                </Badge>
              </div>
              {schedule.recipient_name && (
                <p className="text-xs text-muted-foreground truncate">{schedule.recipient_name}</p>
              )}
              <p className="text-xs text-muted-foreground mt-0.5">
                Next: {formatDate(schedule.next_send_at)}
                {schedule.last_sent_at && ` · Last: ${formatDate(schedule.last_sent_at)}`}
              </p>
            </div>
            <div className="flex items-center gap-1 ml-2 shrink-0">
              <Switch
                checked={!!schedule.enabled}
                onCheckedChange={(checked) =>
                  updateReport.mutate(
                    { reportId: schedule.id, enabled: checked },
                    {
                      onSuccess: () => toast.success(checked ? 'Report enabled' : 'Report paused'),
                      onError: () => toast.error('Failed to update'),
                    },
                  )
                }
              />
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                disabled={sendReport.isPending}
                onClick={() =>
                  sendReport.mutate(schedule.id, {
                    onSuccess: (r) => {
                      if (r.ok) toast.success('Report sent')
                      else toast.error(r.error || 'Send failed')
                    },
                    onError: () => toast.error('Failed to send report'),
                  })
                }
                title="Send now"
              >
                <Send className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-destructive"
                onClick={() =>
                  deleteReport.mutate(schedule.id, {
                    onSuccess: () => toast.success('Report schedule deleted'),
                    onError: () => toast.error('Failed to delete'),
                  })
                }
                title="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}

        {/* History toggle */}
        {history.length > 0 && (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs text-muted-foreground"
              onClick={() => setShowHistory(!showHistory)}
            >
              {showHistory ? <ChevronUp className="mr-1 h-3 w-3" /> : <ChevronDown className="mr-1 h-3 w-3" />}
              {showHistory ? 'Hide' : 'Show'} Send History ({history.length})
            </Button>
            {showHistory && (
              <div className="space-y-1">
                {history.slice(0, 10).map((item) => (
                  <div key={item.id} className="flex items-center justify-between text-xs py-1 border-b last:border-0">
                    <div className="min-w-0 flex-1">
                      <span className="text-muted-foreground">{formatRelative(item.sent_at)}</span>
                      <span className="mx-1.5">·</span>
                      <span className="truncate">{item.recipient_email}</span>
                    </div>
                    <Badge
                      variant={item.status === 'sent' ? 'default' : 'destructive'}
                      className="text-[10px] h-4 px-1.5 shrink-0"
                    >
                      {item.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
